import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AliasBadge, StatusBadge, TrustBadge } from '../components/Badges';
import { TrustLevelBadge } from '../components/TrustLevelBadge';
import SuburbAutocomplete from '../components/SuburbAutocomplete';
import { Plus, Tag, DollarSign, Package, Upload, FileText, FileCheck, MessageSquare, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Equipment Finance', 'Business Loans', 'Asset Finance', 'Personal Loans',
  'Mortgage', 'Solar', 'Insurance', 'Auto Finance',
];

// Controlled vocab — mirrors backend LEAD_SOURCES
const SOURCES = [
  { key: 'google_ads', label: 'Google Ads' },
  { key: 'meta_ads', label: 'Meta (Facebook) Ads' },
  { key: 'tiktok_ads', label: 'TikTok Ads' },
  { key: 'linkedin_ads', label: 'LinkedIn Ads' },
  { key: 'organic_seo', label: 'Organic SEO' },
  { key: 'website_form', label: 'Website Form' },
  { key: 'referral', label: 'Referral' },
  { key: 'partner_referral', label: 'Partner Referral' },
  { key: 'cold_calling', label: 'Cold Calling' },
  { key: 'warm_calling', label: 'Warm Calling' },
  { key: 'sms_campaign', label: 'SMS Campaign' },
  { key: 'email_campaign', label: 'Email Campaign' },
  { key: 'trade_show', label: 'Trade Show' },
  { key: 'walk_in', label: 'Walk-in' },
  { key: 'repeat_customer', label: 'Repeat Customer' },
  { key: 'purchased_db', label: 'Purchased Database' },
  { key: 'affiliate', label: 'Affiliate' },
  { key: 'broker_network', label: 'Broker Network' },
  { key: 'other', label: 'Other' },
];
const CAMPAIGN_TYPES = ['search', 'display', 'social', 'video', 'email', 'sms', 'telemarketing', 'other'];
const INTENT_LEVELS = [
  { key: 'information_request', label: 'Information Request' },
  { key: 'quote_requested', label: 'Quote Requested' },
  { key: 'callback_requested', label: 'Callback Requested' },
  { key: 'appointment_requested', label: 'Appointment Requested' },
  { key: 'ready_to_buy', label: 'Ready to Buy' },
];
const VERIFY_LEVELS = [
  { key: 'email_verified', label: 'Email Verified' },
  { key: 'phone_verified', label: 'Phone Verified' },
  { key: 'sms_verified', label: 'SMS Verified' },
  { key: 'business', label: 'Business Verified' },
];
const REGIONS = ['Australia-wide', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

export default function SellerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myLeads, setMyLeads] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [buyerRequests, setBuyerRequests] = useState([]);
  const [matches, setMatches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [bidTarget, setBidTarget] = useState(null); // match_id currently being bid on
  const [bidPrice, setBidPrice] = useState('');
  const [bidNote, setBidNote] = useState('');

  const [category, setCategory] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryData, setSelectedCategoryData] = useState(null);
  
  // Opportunity value fields
  const [opportunityValueMetric, setOpportunityValueMetric] = useState('');
  const [opportunityAmount, setOpportunityAmount] = useState('');
  const [opportunityAmountMin, setOpportunityAmountMin] = useState('');
  const [opportunityAmountMax, setOpportunityAmountMax] = useState('');
  const [opportunityCertainty, setOpportunityCertainty] = useState('exact');
  const [opportunityCurrency, setOpportunityCurrency] = useState('AUD');
  
  const [sourceType, setSourceType] = useState('');
  const [verifyLevel, setVerifyLevel] = useState('');
  const [price, setPrice] = useState('');
  const [leadAge, setLeadAge] = useState('');
  const [region, setRegion] = useState('');
  const [suburb, setSuburb] = useState('');
  const [postcode, setPostcode] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [extraDetails, setExtraDetails] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  // Lead Passport fields
  const [exclusivity, setExclusivity] = useState('shared');
  const [sharedCap, setSharedCap] = useState(3);
  const [campaignType, setCampaignType] = useState('');
  const [intentLevel, setIntentLevel] = useState('information_request');
  const [campaignId, setCampaignId] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [submissionTs, setSubmissionTs] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [businessVerified, setBusinessVerified] = useState(false);
  const [consentCaptured, setConsentCaptured] = useState(false);
  const [privacyCompliant, setPrivacyCompliant] = useState(false);
  const [dncChecked, setDncChecked] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);
  
  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };
  
  const handleCategoryChange = (catId) => {
    const cat = categories.find(c => c.category_id === catId);
    if (cat) {
      setCategoryId(catId);
      setCategory(cat.label);
      setSelectedCategoryData(cat);
      setServiceId('');
      setOpportunityValueMetric('');
    }
  };
  
  const handleServiceChange = (svcId) => {
    setServiceId(svcId);
    if (selectedCategoryData) {
      const svc = selectedCategoryData.services[svcId];
      if (svc && svc.value_metric) {
        setOpportunityValueMetric(svc.value_metric);
      }
    }
  };

  const fetchData = async () => {
    try {
      const [leadsRes, txnRes, reqRes, matchRes, roomsRes] = await Promise.all([
        api.get('/seller/leads'),
        api.get('/transactions/my'),
        api.get('/buyer-requests'),
        api.get('/seller/matches'),
        api.get('/rooms/my')
      ]);
      setMyLeads(leadsRes.data || []);
      setTransactions(txnRes.data);
      setBuyerRequests(reqRes.data);
      setMatches(matchRes.data || []);
      setRooms(roomsRes.data || []);
    } catch (e) {
      console.error('SellerDashboard fetch failed', e);
    }
  };

  const submitBid = async (e) => {
    e.preventDefault();
    if (!bidTarget) return;
    try {
      await api.post(`/seller/matches/${bidTarget}/bid`, {
        bid_price: parseFloat(bidPrice),
        bid_note: bidNote || null,
      });
      toast.success('Bid placed');
      setBidTarget(null); setBidPrice(''); setBidNote('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bid failed');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/leads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFile({ path: res.data.file_path, name: res.data.original_filename });
      toast.success('File uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build opportunity_values
      const opportunity_values = {};
      if (opportunityValueMetric) {
        const valueData = {
          value_metric: opportunityValueMetric,
          currency: opportunityCurrency,
          certainty: opportunityCertainty,
          source: 'seller_stated',
          verification_status: 'unverified',
          timestamp: new Date().toISOString(),
        };
        
        if (opportunityCertainty === 'exact' && opportunityAmount) {
          valueData.amount_dollars = parseInt(opportunityAmount);
        } else if (opportunityCertainty === 'range') {
          if (opportunityAmountMin) valueData.amount_min_dollars = parseInt(opportunityAmountMin);
          if (opportunityAmountMax) valueData.amount_max_dollars = parseInt(opportunityAmountMax);
        } else if (opportunityCertainty === 'unknown') {
          // No amounts needed
        } else if (opportunityCertainty === 'estimated' && opportunityAmount) {
          valueData.amount_dollars = parseInt(opportunityAmount);
        }
        
        opportunity_values[opportunityValueMetric] = valueData;
      }
      
      await api.post('/leads', {
        category,
        service_id: serviceId || null,
        category_id: categoryId || null,
        opportunity_values: Object.keys(opportunity_values).length > 0 ? opportunity_values : null,
        source_type: sourceType,
        verification_level: verifyLevel,
        price: parseFloat(price),
        lead_age_hours: parseInt(leadAge),
        region,
        suburb: suburb || null,
        postcode: postcode || null,
        masked_preview: {
          category,
          state: region,
          suburb: suburb || null,
          postcode: postcode || null,
          verification: verifyLevel,
          source: sourceType,
        },
        full_data: {
          name: contactName,
          phone: contactPhone,
          email: contactEmail,
          notes: extraDetails,
          file: uploadedFile?.name || null,
        },
        // Lead Passport fields
        exclusivity,
        shared_cap: exclusivity === 'shared' ? parseInt(sharedCap) || 3 : (exclusivity === 'exclusive' ? 1 : 10),
        campaign_type: campaignType || null,
        intent_level: intentLevel,
        source_evidence: {
          ...(campaignId ? { campaign_id: campaignId } : {}),
          ...(landingUrl ? { landing_url: landingUrl } : {}),
          ...(submissionTs ? { submission_timestamp: submissionTs } : {}),
        },
        verification_flags: {
          phone_verified: phoneVerified,
          email_verified: emailVerified,
          business_verified: businessVerified,
        },
        compliance: {
          consent_captured: consentCaptured,
          privacy_compliant: privacyCompliant,
          do_not_contact_checked: dncChecked,
          verification_date: new Date().toISOString().slice(0, 10),
        },
      });
      toast.success('Lead listed with a Lead Passport');
      setOpen(false);
      // reset
      setCategory(''); setSourceType(''); setVerifyLevel(''); setPrice('');
      setLeadAge(''); setRegion(''); setSuburb(''); setPostcode(''); setContactName(''); setContactPhone('');
      setContactEmail(''); setExtraDetails(''); setUploadedFile(null);
      setExclusivity('shared'); setSharedCap(3); setCampaignType('');
      setIntentLevel('information_request'); setCampaignId(''); setLandingUrl(''); setSubmissionTs('');
      setPhoneVerified(false); setEmailVerified(false); setBusinessVerified(false);
      setConsentCaptured(false); setPrivacyCompliant(false); setDncChecked(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to list lead');
    }
  };

  const totalEarnings = transactions.filter(t => t.payment_status === 'paid' && t.seller_id === user.user_id).reduce((sum, t) => sum + t.amount * 0.8, 0);
  const soldCount = myLeads.filter(l => l.status === 'sold').length;
  const availableCount = myLeads.filter(l => l.status === 'available').length;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <AliasBadge alias={user.alias} />
              <TrustLevelBadge level={user.trust_level || 0} size="sm" />
              <TrustBadge score={user.trust_score} size="sm" />
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Seller Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">List leads · Upload source · Track payouts</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/seller/sources')}
              className="border-gray-300"
              data-testid="manage-sources-btn"
            >
              <FileCheck className="w-4 h-4 mr-2" /> Source Profiles
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="list-lead-btn">
                  <Plus className="w-4 h-4 mr-2" /> List New Lead
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>List New Lead</DialogTitle>
                <DialogDescription>
                  Buyers see only the masked preview. Contact details unlock after they purchase.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category *</Label>
                    <Select value={categoryId} onValueChange={handleCategoryChange} required>
                      <SelectTrigger data-testid="lead-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => <SelectItem key={cat.category_id} value={cat.category_id}>{cat.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedCategoryData && (
                    <div>
                      <Label>Service *</Label>
                      <Select value={serviceId} onValueChange={handleServiceChange} required>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(selectedCategoryData.services).map(([svcId, svc]) => (
                            <SelectItem key={svcId} value={svcId}>{svc.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {/* Opportunity Value */}
                {opportunityValueMetric && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded space-y-2">
                    <div className="text-xs font-semibold text-gray-900">Opportunity Value</div>
                    <Select value={opportunityCertainty} onValueChange={setOpportunityCertainty}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exact">Exact</SelectItem>
                        <SelectItem value="estimated">Estimated</SelectItem>
                        <SelectItem value="range">Range</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                    {opportunityCertainty !== 'unknown' && (
                      <div className="grid grid-cols-2 gap-2">
                        {opportunityCertainty === 'range' ? (
                          <>
                            <Input
                              type="number"
                              value={opportunityAmountMin}
                              onChange={(e) => setOpportunityAmountMin(e.target.value)}
                              placeholder="Min A$"
                              className="h-8 text-xs"
                            />
                            <Input
                              type="number"
                              value={opportunityAmountMax}
                              onChange={(e) => setOpportunityAmountMax(e.target.value)}
                              placeholder="Max A$"
                              className="h-8 text-xs"
                            />
                          </>
                        ) : (
                          <Input
                            type="number"
                            value={opportunityAmount}
                            onChange={(e) => setOpportunityAmount(e.target.value)}
                            placeholder="Amount A$"
                            className="h-8 text-xs"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category (Legacy)</Label>
                    <Select value={category} onValueChange={setCategory} required>
                      <SelectTrigger data-testid="lead-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Region</Label>
                    <Select value={region} onValueChange={setRegion} required>
                      <SelectTrigger data-testid="lead-region-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Suburb <span className="text-gray-400 text-xs">(optional)</span></Label>
                    <SuburbAutocomplete
                      value={suburb}
                      onChange={setSuburb}
                      onSelect={(item) => {
                        setSuburb(item.suburb);
                        setPostcode(item.postcode);
                        if (item.state) setRegion(item.state);
                      }}
                      testid="lead-suburb-input"
                    />
                  </div>
                  <div>
                    <Label>Postcode <span className="text-gray-400 text-xs">(recommended)</span></Label>
                    <Input
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="e.g. 2010"
                      inputMode="numeric"
                      maxLength={4}
                      data-testid="lead-postcode-input"
                    />
                    <div className="text-[11px] text-gray-500 mt-1">Lets buyers with radius rules find this lead.</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Lead Source</Label>
                    <Select value={sourceType} onValueChange={setSourceType} required>
                      <SelectTrigger data-testid="lead-source-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SOURCES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Verification</Label>
                    <Select value={verifyLevel} onValueChange={setVerifyLevel} required>
                      <SelectTrigger data-testid="lead-verify-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{VERIFY_LEVELS.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Price ($)</Label>
                    <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} required data-testid="lead-price-input" />
                  </div>
                  <div>
                    <Label>Lead Age (hours)</Label>
                    <Input type="number" value={leadAge} onChange={(e) => setLeadAge(e.target.value)} required data-testid="lead-age-input" />
                  </div>
                </div>

                {/* Lead Passport section — the differentiator */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono mb-3">Lead Passport · Provenance</div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <Label>Exclusivity</Label>
                      <Select value={exclusivity} onValueChange={setExclusivity}>
                        <SelectTrigger data-testid="lead-exclusivity-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exclusive">Exclusive (1 buyer)</SelectItem>
                          <SelectItem value="shared">Shared (multi-buyer capped)</SelectItem>
                          <SelectItem value="multi_buy">Multi-buy (unrestricted)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {exclusivity === 'shared' && (
                      <div>
                        <Label>Max Shared Buyers</Label>
                        <Input type="number" min="2" max="10" value={sharedCap} onChange={(e) => setSharedCap(e.target.value)} data-testid="lead-shared-cap-input" />
                      </div>
                    )}
                    <div>
                      <Label>Campaign Type</Label>
                      <Select value={campaignType} onValueChange={setCampaignType}>
                        <SelectTrigger data-testid="lead-campaign-type-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent>{CAMPAIGN_TYPES.map(c => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Intent Level</Label>
                      <Select value={intentLevel} onValueChange={setIntentLevel}>
                        <SelectTrigger data-testid="lead-intent-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{INTENT_LEVELS.map(i => <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs">Campaign ID / UTM (optional)</Label>
                      <Input value={campaignId} onChange={(e) => setCampaignId(e.target.value)} placeholder="G-123 · fb_utm_id" data-testid="lead-campaign-id-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Landing / Form URL (optional)</Label>
                      <Input value={landingUrl} onChange={(e) => setLandingUrl(e.target.value)} placeholder="https://..." data-testid="lead-landing-url-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Submission Timestamp (optional)</Label>
                    <Input type="datetime-local" value={submissionTs} onChange={(e) => setSubmissionTs(e.target.value)} data-testid="lead-submission-ts-input" />
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono col-span-2 mt-2">Verification</div>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={phoneVerified} onChange={e => setPhoneVerified(e.target.checked)} data-testid="lead-phone-verified" /> Phone verified</label>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={emailVerified} onChange={e => setEmailVerified(e.target.checked)} data-testid="lead-email-verified" /> Email verified</label>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={businessVerified} onChange={e => setBusinessVerified(e.target.checked)} data-testid="lead-business-verified" /> Business verified</label>

                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono col-span-2 mt-2">Compliance</div>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={consentCaptured} onChange={e => setConsentCaptured(e.target.checked)} data-testid="lead-consent-captured" /> Consent captured</label>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={privacyCompliant} onChange={e => setPrivacyCompliant(e.target.checked)} data-testid="lead-privacy-compliant" /> Privacy compliant</label>
                    <label className="inline-flex items-center gap-2 col-span-2"><input type="checkbox" checked={dncChecked} onChange={e => setDncChecked(e.target.checked)} data-testid="lead-dnc-checked" /> Do-Not-Contact list checked</label>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">Contact Details (Unlocked After Purchase)</div>
                  <div className="space-y-3">
                    <div>
                      <Label>Contact Name</Label>
                      <Input value={contactName} onChange={(e) => setContactName(e.target.value)} required data-testid="lead-contact-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Phone</Label>
                        <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} required data-testid="lead-contact-phone" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required data-testid="lead-contact-email" />
                      </div>
                    </div>
                    <div>
                      <Label>Notes / Extra Details</Label>
                      <Textarea value={extraDetails} onChange={(e) => setExtraDetails(e.target.value)} data-testid="lead-notes" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <Label className="mb-2 block">Source Proof (Optional)</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{uploading ? 'Uploading...' : 'Upload File'}</span>
                      <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="lead-file-upload" />
                    </label>
                    {uploadedFile && (
                      <span className="text-xs text-gray-600 flex items-center gap-1"><FileText className="w-3 h-3" />{uploadedFile.name}</span>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="submit-lead-btn">List Lead</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Verification promo (shown until Level 1 KYC) */}
        {(user.trust_level || 0) < 1 && (
          <div className="mb-6 border border-[#0055FF] bg-blue-50 rounded-lg p-4 flex items-center gap-4" data-testid="seller-verify-banner">
            <ShieldCheck className="w-6 h-6 text-[#0055FF] flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-gray-900">Verify to start selling</div>
              <div className="text-xs text-gray-700 mt-0.5">Identity verification (KYC) is required to list leads. Business verification (KYB) unlocks priority placement and lower fees.</div>
            </div>
            <Button onClick={() => navigate('/verification')} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="seller-verify-btn">
              Start verification
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden mb-8">
          <Stat label="Available" value={availableCount} icon={Package} />
          <Stat label="Sold" value={soldCount} icon={Tag} />
          <Stat label="Total Listings" value={myLeads.length} icon={FileText} />
          <Stat label="Net Earnings" value={`$${totalEarnings.toFixed(2)}`} icon={DollarSign} />
        </div>

        {/* My listings */}
        <section className="mb-12">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">My Listings</h2>
          {myLeads.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
              No listings yet. Click &ldquo;List New Lead&rdquo; to add your first.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="my-listings">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Lead ID</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Region</th>
                    <th className="px-4 py-3">Price</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {myLeads.map(lead => (
                    <tr key={lead.lead_id} className="text-sm hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{lead.lead_id}</td>
                      <td className="px-4 py-3 font-medium">{lead.category}</td>
                      <td className="px-4 py-3 text-gray-600">{lead.region}</td>
                      <td className="px-4 py-3 font-mono">${lead.price.toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Demand Alerts — matches on buyer requests that fit my categories */}
        <section className="mb-12" data-testid="seller-demand-alerts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-gray-900">Demand Alerts</h2>
            <span className="text-xs uppercase tracking-wider text-gray-500 font-mono">
              {matches.filter(m => m.status === 'pending' || m.status === 'bid_placed').length} open
            </span>
          </div>
          {matches.filter(m => m.status === 'pending' || m.status === 'bid_placed').length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500 text-sm">
              No demand alerts yet. As buyers post requests in your categories, they&apos;ll appear here.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {matches
                .filter(m => m.status === 'pending' || m.status === 'bid_placed')
                .sort((a, b) => (a.status === 'pending' && b.status !== 'pending' ? -1 : 0))
                .map(m => (
                <div
                  key={m.match_id}
                  data-testid={`match-card-${m.match_id}`}
                  className="bg-white border border-gray-200 rounded-lg p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <AliasBadge alias={m.buyer_alias} />
                    <StatusBadge status={m.status} />
                  </div>
                  <h3 className="font-heading text-base font-semibold mb-3">
                    Wants: {m.request_snapshot?.category_wanted}
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{m.request_snapshot?.location}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Volume</span><span className="font-mono">{m.request_snapshot?.volume_per_week}/week</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Target</span><span className="font-mono font-semibold">${Number(m.request_snapshot?.target_price || 0).toFixed(2)}</span></div>
                    {m.bid_price != null && (
                      <div className="flex justify-between text-[#0055FF]"><span>Your bid</span><span className="font-mono font-semibold">${Number(m.bid_price).toFixed(2)}</span></div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
                    {m.request_snapshot?.criteria}
                  </div>
                  {m.status !== 'accepted' && m.status !== 'declined' && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        data-testid={`place-bid-btn-${m.match_id}`}
                        onClick={() => { setBidTarget(m.match_id); setBidPrice(m.bid_price || ''); setBidNote(m.bid_note || ''); }}
                      >
                        {m.status === 'bid_placed' ? 'Update Bid' : 'Place Bid'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bid dialog */}
        <Dialog open={!!bidTarget} onOpenChange={(o) => !o && setBidTarget(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Place Bid</DialogTitle>
              <DialogDescription>Set the price you can supply this lead type at, per lead.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submitBid} className="space-y-4">
              <div>
                <Label>Bid Price ($ per lead)</Label>
                <Input type="number" step="0.01" min="0.01" value={bidPrice} onChange={(e) => setBidPrice(e.target.value)} required data-testid="bid-price-input" />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea value={bidNote} onChange={(e) => setBidNote(e.target.value)} placeholder="Fresh Sydney solar batch — phone + email verified" data-testid="bid-note-input" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="submit-bid-btn">Submit Bid</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Won Deals — buyer accepted my bid, deal room ready */}
        {matches.some(m => m.status === 'accepted' && m.room_id) && (
          <section className="mb-12" data-testid="seller-won-deals">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Won Deals</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {matches.filter(m => m.status === 'accepted' && m.room_id).map(m => (
                <div
                  key={m.match_id}
                  data-testid={`won-card-${m.match_id}`}
                  className="bg-white border border-gray-200 rounded-lg p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <AliasBadge alias={m.buyer_alias} />
                    <StatusBadge status={m.status} />
                  </div>
                  <h3 className="font-heading text-base font-semibold mb-2">
                    {m.request_snapshot?.category_wanted}
                  </h3>
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-gray-500">Your bid</span>
                    <span className="font-mono font-semibold text-[#0055FF]">${Number(m.bid_price || 0).toFixed(2)}</span>
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    onClick={() => navigate(`/rooms/${m.room_id}`)}
                    data-testid={`open-room-btn-${m.match_id}`}
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Open Deal Room
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Buyer requests */}
        <section>
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Active Buyer Requests</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {buyerRequests.map(req => (
              <div key={req.request_id} className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="flex items-start justify-between mb-3">
                  <AliasBadge alias={req.buyer_alias} />
                  <StatusBadge status={req.status} />
                </div>
                <h3 className="font-heading text-base font-semibold mb-3">Wants: {req.category_wanted}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{req.location}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Volume</span><span className="font-mono">{req.volume_per_week}/week</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Target Price</span><span className="font-mono font-semibold">${req.target_price.toFixed(2)}</span></div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">{req.criteria}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Active Deal Rooms */}
        {rooms.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Active Deal Rooms</h2>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="seller-rooms-list">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Room ID</th>
                    <th className="px-4 py-3">Lead</th>
                    <th className="px-4 py-3">Buyer</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rooms.map(r => (
                    <tr key={r.room_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{r.room_id}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.lead_id}</td>
                      <td className="px-4 py-3">{r.buyer_alias ? <AliasBadge alias={r.buyer_alias} /> : <span className="text-gray-400 text-xs">—</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/rooms/${r.room_id}`)} data-testid={`open-room-${r.room_id}`}>
                          <MessageSquare className="w-3.5 h-3.5 mr-1" /> Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-white p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
    </div>
    <div className="font-mono text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);
